import React, { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import RatingDialog from './RatingDialog';
import { selectPlayer, selectHeatmap } from '../../slices/gameStatsSlice';
import computePlayerRatingsForMatch, { sanitizeAvatarText } from './playerRatingsShared';

const formatNumber = (value, digits = 1) => {
  const num = value == null ? NaN : Number(value);
  return Number.isFinite(num) ? num.toFixed(digits) : '—';
};

function PlayerRatings() {
  const matches = useSelector((state) => state.gameStats.matches) || [];
  const mtc = useSelector((state) => state.gameStats.selectedMatch) || 0;
  const dispatch = useDispatch();
  const [ratingPlayer, setRatingPlayer] = useState(null);

  const currentMatch = matches[mtc] || null;

  const columns = useMemo(() => computePlayerRatingsForMatch(currentMatch), [currentMatch]);

  const handleClick = (nick) => {
    if (!nick) return;
    dispatch(selectPlayer(nick));
    dispatch(selectHeatmap('Heatmap'));
    setRatingPlayer(nick);
  };

  const renderColumn = (teamKey) => {
    const players = columns[teamKey] || [];
    const title = teamKey === 'red' ? 'Red Team' : 'Blue Team';
    return (
      <Grid item xs={12} md={6} key={teamKey}>
        <Paper elevation={0} className={`gs-rating-column ${teamKey}`}>
          <div className="gs-rating-column-header">{title}</div>
          <Divider light className="gs-rating-divider" />
          <div className="gs-rating-list">
            {players.map((player) => {
              const isMvp = columns.mvpNick === player.nick;
              const badge = isMvp ? 'MVP' : null;
              const ratingLabel = `${formatNumber(player.rating, 1)}/10`;
              const initials = Array.from(String(player.nick || '?').trim()).slice(0, 2).join('').toUpperCase();
              const avatarText = sanitizeAvatarText(player.avatar) || sanitizeAvatarText(player.nick) || initials;
              return (
                <button
                  type="button"
                  key={player.nick}
                  className={`gs-rating-entry team-${teamKey}${isMvp ? ' highlight' : ''}`}
                  onClick={() => handleClick(player.nick)}
                  title={[player.provisional ? 'Provisional rating' : 'Haxball Impact rating', ...(player.breakdown?.contributions || []).filter(item => Math.abs(item.value) >= 0.05).map(item => `${item.label}: ${item.value > 0 ? '+' : ''}${item.value.toFixed(2)}`)].join('\n')}
                >
                  <div className="gs-rating-avatar">
                    <div className={`gs-rating-avatar-circle team-${teamKey}${isMvp ? ' is-mvp' : ''}`}>
                      {avatarText}
                    </div>
                  </div>
                  <div className="gs-rating-playerinfo">
                    <div className="gs-rating-name">{player.nick}</div>
                    {badge && <div className="gs-rating-badge">{badge}</div>}
                    {player.provisional && <div className="gs-rating-badge">Provisional</div>}
                  </div>
                  <div className="gs-rating-score">{ratingLabel}</div>
                </button>
              );
            })}
            {!players.length && (
              <div className="gs-rating-empty-team">No players for this side.</div>
            )}
          </div>
        </Paper>
      </Grid>
    );
  };

  if (!columns.red.length && !columns.blue.length) {
    return (
      <div className="gs-pro-rating-empty">
        Rating data is unavailable for this match.
      </div>
    );
  }

  return (
    <div className="gs-rating-grid">
      <Typography variant="body2" sx={{ color: '#cbd5e1', mb: 2 }}>
        Haxball Impact v6 · Select a player to see what contributed to their rating.
      </Typography>
      {ratingPlayer !== null && <RatingDialog open initialPlayer={ratingPlayer} onClose={() => setRatingPlayer(null)} />}
      <Grid container spacing={2}>
        {renderColumn('red')}
        {renderColumn('blue')}
      </Grid>
    </div>
  );
}

export default PlayerRatings;
